import { Link } from 'react-router-dom';
import { useLanguage } from '../i18n';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { ArrowRight, Check, Sparkles, Building2, Users, Zap } from 'lucide-react';

export default function LandingPage() {
  const { t } = useLanguage();
  const l = t.landing;

  return (
    <div className="min-h-screen bg-paper text-ink">
      {/* Navbar */}
      <nav className="sticky top-0 z-40 bg-white/90 backdrop-blur-sm border-b border-cloud">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-red-500 flex items-center justify-center">
              <Building2 size={18} className="text-white" />
            </div>
            <span className="text-xl font-bold tracking-tight">{t.brand}</span>
          </div>
          <div className="flex items-center gap-3 sm:gap-4">
            <LanguageSwitcher />
            <Link to="/login" className="text-sm font-medium text-steel hover:text-black transition-colors">
              {t.nav.login}
            </Link>
            <Link
              to="/register"
              className="text-sm font-medium px-4 py-2 bg-black text-white hover:bg-steel transition-colors"
            >
              {t.nav.register}
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-cloud">
        <div className="absolute inset-0 pointer-events-none">
          <div className="gradient-orb gradient-orb-red w-[28rem] h-[28rem] -top-40 -right-32" style={{ animationDuration: '12s', animationDelay: '0s' }} />
          <div className="gradient-orb gradient-orb-blue w-96 h-96 top-1/3 -left-32" style={{ animationDuration: '14s', animationDelay: '-3s' }} />
          <div className="gradient-orb gradient-orb-red w-64 h-64 bottom-0 left-1/4" style={{ animationDuration: '9s', animationDelay: '-6s' }} />
          <div className="gradient-orb gradient-orb-blue w-72 h-72 top-1/2 right-1/4" style={{ animationDuration: '11s', animationDelay: '-9s' }} />
        </div>

        <div className="relative max-w-6xl mx-auto px-6 py-24 md:py-32">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-red-50 border border-red-100 text-red-700 text-xs font-semibold uppercase tracking-wider mb-6">
              <Sparkles size={12} />
              {l.badge}
            </div>
            <h1 className="text-5xl md:text-7xl font-extrabold font-display text-black mb-6">
              {l.headline}
            </h1>
            <p className="text-lg md:text-xl text-slate mb-8 max-w-2xl leading-relaxed">
              {l.subheadline}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/register"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-red-500 hover:bg-red-600 text-white font-semibold transition-colors"
              >
                {l.ctaPrimary}
                <ArrowRight size={18} />
              </Link>
              <Link
                to="/login"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-cloud bg-white hover:bg-mist text-black font-semibold transition-colors"
              >
                {l.ctaSecondary}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Subscription cards */}
      <section className="py-20 md:py-28">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold font-display text-black mb-4">{l.pricingTitle}</h2>
            <p className="text-slate max-w-2xl mx-auto">{l.pricingSubtitle}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <PlanCard plan="starter" orbColor="blue" />
            <PlanCard plan="founder" orbColor="red" popular />
            <PlanCard plan="scale" orbColor="blue" />
          </div>

          <p className="text-center text-xs text-ash mt-8">{l.disclaimer}</p>
        </div>
      </section>

      {/* Features grid */}
      <section className="py-20 bg-white border-y border-cloud">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <Feature
              icon={<Zap size={24} className="text-red-500" />}
              title={l.features.fast.title}
              text={l.features.fast.text}
            />
            <Feature
              icon={<Building2 size={24} className="text-blue-600" />}
              title={l.features.operator.title}
              text={l.features.operator.text}
            />
            <Feature
              icon={<Users size={24} className="text-red-500" />}
              title={l.features.scale.title}
              text={l.features.scale.text}
            />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-cloud">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-black flex items-center justify-center">
              <Building2 size={14} className="text-white" />
            </div>
            <span className="font-bold text-sm">{t.brand}</span>
          </div>
          <p className="text-xs text-ash">
            © {new Date().getFullYear()} {t.brand}. {l.footer}
          </p>
        </div>
      </footer>
    </div>
  );
}

function PlanCard({
  plan,
  orbColor,
  popular = false,
}: {
  plan: 'starter' | 'founder' | 'scale';
  orbColor: 'red' | 'blue';
  popular?: boolean;
}) {
  const { t } = useLanguage();
  const data = t.landing.plans[plan];
  const price = data.price;

  return (
    <div className={`relative bg-white border ${popular ? 'border-red-500' : 'border-cloud'} p-8 flex flex-col`}>
      {popular && 'popular' in data && (
        <div className="absolute top-0 right-0 bg-red-500 text-white text-xs font-bold uppercase tracking-wider px-3 py-1">
          {data.popular}
        </div>
      )}

      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className={`gradient-orb gradient-orb-${orbColor} w-72 h-72 -top-20 -right-20`}
          style={{ animationDuration: popular ? '10s' : '12s', animationDelay: popular ? '0s' : '-6s' }}
        />
        <div
          className={`gradient-orb gradient-orb-${orbColor === 'red' ? 'blue' : 'red'} w-48 h-48 -bottom-12 -left-12 opacity-70`}
          style={{ animationDuration: popular ? '13s' : '15s', animationDelay: popular ? '-4s' : '-10s' }}
        />
      </div>

      <div className="relative z-10 flex-1">
        <h3 className="text-xl font-bold text-black mb-1">{data.name}</h3>
        <p className="text-sm text-slate mb-4">{data.description}</p>
        <div className="flex items-baseline gap-1 mb-6">
          <span className="text-4xl font-extrabold text-black">{price}</span>
          {'period' in data && (data as { period?: string }).period && (
            <span className="text-sm text-ash">{(data as { period?: string }).period}</span>
          )}
        </div>

        <ul className="space-y-3 mb-8">
          {data.features.map((feature: string) => (
            <li key={feature} className="flex items-start gap-3 text-sm text-steel">
              <Check size={16} className={`shrink-0 mt-0.5 ${popular ? 'text-red-500' : 'text-blue-600'}`} />
              {feature}
            </li>
          ))}
        </ul>
      </div>

      <Link
        to={`/register?plan=${plan}`}
        className={`relative z-10 w-full text-center py-3 font-semibold transition-colors ${
          popular ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-black hover:bg-steel text-white'
        }`}
      >
        {data.cta}
      </Link>
    </div>
  );
}

function Feature({ icon, title, text }: { icon: React.ReactNode; title: string; text: string }) {
  return (
    <div className="flex flex-col gap-3">
      <div className="w-10 h-10 bg-mist flex items-center justify-center border border-cloud">{icon}</div>
      <h3 className="text-lg font-bold text-black">{title}</h3>
      <p className="text-sm text-slate leading-relaxed">{text}</p>
    </div>
  );
}
